import { useState } from 'react';
import type { ShoppingLine } from '../../domain/types.js';

export function TickRow({
  line,
  rowKey,
  ticked,
  onToggle,
  onRemove,
}: {
  line: ShoppingLine;
  /** Supplied by the caller. Never derived here — see ShoppingList. */
  rowKey: string;
  ticked: boolean;
  onToggle: () => void;
  /**
   * Only a manual extra can be removed — a derived line is there because a
   * meal needs it. Null for everything else.
   */
  onRemove: (() => void) | null;
}): React.JSX.Element {
  const [showWhy, setShowWhy] = useState(false);

  return (
    <li className="tick-li">
      <button
        type="button"
        className="tick"
        data-line={rowKey}
        aria-pressed={ticked}
        onClick={() => {
          if (!ticked && typeof navigator.vibrate === 'function') navigator.vibrate(12);
          onToggle();
        }}
      >
        <span className="box" aria-hidden="true">
          <svg viewBox="0 0 24 24"><path d="M4 12.5l5.5 5.5L20 7" /></svg>
        </span>
        <span className="tick-text">
          <span className="what">{line.label}</span>
          {/*
            Branch on isUnpacked, NOT on packLabel. A manual extra has no pack
            label and is not unpacked — branching on the label painted every
            "bin bags" and "wine" in the red no-pack-data flag, which is the
            app's one safety signal. Drown it in noise and it stops working
            before the line that genuinely needs it ever appears.
          */}
          {line.isUnpacked ? (
            <span className="pack unpacked">
              {line.requiredQty}{line.unit} — no pack size known for this shop
            </span>
          ) : line.packLabel ? (
            <span className="pack">{line.packLabel}</span>
          ) : null}
        </span>
      </button>

      {/*
        One slot, two jobs. A derived line explains itself; a manual extra
        offers to go away. Typing the wrong thing and being stuck with it all
        week is exactly the friction that sends someone back to paper.
      */}
      {line.sources.length > 0 ? (
        <button
          type="button"
          className="why"
          aria-label={`Why is ${line.label} on the list?`}
          onClick={() => setShowWhy((v) => !v)}
        >
          ?
        </button>
      ) : onRemove ? (
        <button
          type="button"
          className="why"
          aria-label={`Remove ${line.label}`}
          onClick={onRemove}
        >
          &times;
        </button>
      ) : null}

      {showWhy ? <p className="why-note">{line.sources.join(' · ')}</p> : null}
    </li>
  );
}
